import { computed, ComputedRef, reactive } from "vue";
import axios from "axios";

import useNotify from "@Composables/useNotify";
import { ParseServerError, ProcessServerErrors } from "@Modules/shared/Api/ServerErrors";
import type { PendingTeamInvitationData } from "@Modules/teams/Data/Models/PendingTeamInvitation";
import { HttpRouter } from "@Modules/shared/interfaces/HttpRouter";
import type { TypedTeamInvitation, TypedTeamInvitationList, TeamMemberRole } from "@Resources/js/laravel";
import { logError } from "@Resources/js/helpers/logger";

/** Matches the per-address keys the invitation endpoint returns, e.g. "invitations.3". */
const INVITATION_ERROR_KEY = /^invitations\.(\d+)$/;

/**
 * An invitation with no team is an invitation to the organization itself, and those are owned by
 * the organization endpoints rather than the team-scoped ones. Accepting one grants organization
 * membership plus the organization's default team, which the team endpoints cannot do.
 */
function invitationBaseUrl(orgID: string, teamID: string | null): string {
    return teamID
        ? `/app/api/organization/${orgID}/team/${teamID}/invite`
        : `/app/api/organization/${orgID}/users/invite`;
}

/**
 * Re-key the invitation validation errors by the address each one belongs to, so callers can match
 * them against the addresses they submitted.
 *
 * The server keys every per-address message by its submitted position (`invitations.3` is the
 * fourth address sent) but emits them in validator order, not input order — so the index has to be
 * read out of the key rather than inferred from the position in the bag. Batch level keys
 * (`invitations`, `role`, `message`) carry no index and are passed through untouched.
 */
function mapInvitationErrors(
    fieldErrors: Record<string, string[]>,
    emails: string[],
): Record<string, string> {
    return Object.entries(fieldErrors).reduce<Record<string, string>>((errors, [key, messages]) => {
        const index = key.match(INVITATION_ERROR_KEY)?.[1];
        const email = index === undefined ? null : emails[Number(index)];

        errors[email ?? key] = messages[0] ?? "";

        return errors;
    }, {});
}

export type InvitationsApi = {
    invitations: ComputedRef<PendingTeamInvitationData[]>,
    loading: ComputedRef<boolean>,
    errors: ComputedRef<Record<string, string>>,
    fetchInvitations: (args: { orgID: string, teamID?: string | null, clear?: boolean }) => Promise<void>,
    inviteUsers: (args: {
        orgID: string,
        emails: string[],
        teamID?: string | null,
        role?: TeamMemberRole | null,
        message?: string,
    }) => Promise<TypedTeamInvitationList | null>,
    resendInvitation: (args: { orgID: string, teamID?: string | null, invitationID: string }) => Promise<boolean>,
    cancelInvitation: (args: { orgID: string, invitationID: string, teamID?: string | null }) => Promise<boolean>,
    setRouter: (router: HttpRouter<"get" | "post" | "delete">) => InvitationsApi,
    clearInvitations: () => void,
    clearErrors: () => void,
}

/**
 * InvitationsApi talks to the organization and team invitation endpoints. Invitations are a separate
 * resource from organization members, which UsersApi owns.
 */
export default function InvitationsApi(): InvitationsApi {
    const state = reactive<{
        invitations: PendingTeamInvitationData[],
        loading: boolean,
        errors: Record<string, string>,
    }>({
        invitations: [],
        loading: false,
        errors: {},
    });

    let router: HttpRouter<"get" | "post" | "delete"> = axios;
    const { notifySuccess, notifyError } = useNotify();

    function clearErrors() {
        Object.keys(state.errors).forEach((key) => delete state.errors[key]);
    }

    /** Map the server payload into the row the pending table renders. */
    function invitationToPendingInvitation(invitation: TypedTeamInvitation): PendingTeamInvitationData {
        return {
            invitation_id: invitation.invite_id,
            email: invitation.invited_email,
            invited_as: invitation.invited_as || "",
            created_at: invitation.created_at,
            last_sent_at: invitation.last_sent_at,
            team_slug: invitation.team?.slug ?? null,
        };
    }

    /**
     * Report a failed single-invitation request. A cancel refused because the invitation does not
     * belong to the requested scope answers 422 with its reason in the `invitation` key, while a
     * refused resend answers 403 carrying its reason as the response message. Either is shown
     * verbatim in preference to a generic message.
     */
    function notifyInvitationFailure(e: unknown, fallback: string): void {
        const { fieldErrors, serverMessage } = ParseServerError(e);

        if (fieldErrors) {
            state.errors = ProcessServerErrors(fieldErrors);
        }

        notifyError({ text: fieldErrors?.invitation?.[0] || serverMessage || fallback });
    }

    const that = {
        invitations: computed(() => state.invitations),
        loading: computed(() => state.loading),
        errors: computed(() => state.errors),
        clearInvitations: () => state.invitations = [],
        clearErrors,

        /**
         * Fetch the pending invitations for the provided organization. Scoped to a single team when
         * teamID is given, otherwise every team invitation across the organization.
         */
        fetchInvitations: async function ({
            orgID,
            teamID = null,
            clear = true,
        }: { orgID: string, teamID?: string | null, clear?: boolean }) {
            clearErrors();
            if (clear) {
              state.invitations = [];
            }

            state.loading = true;
            const scope = teamID ? `/team/${teamID}` : "";
            const url = `/app/api/organization/${orgID}${scope}/invitations`;

            try {
              const response = await router.get<TypedTeamInvitation[]>(url);

              if (response.status < 200 || response.status >= 300) {
                throw new Error(`Could not fetch invitations for org ${orgID}`);
              }

              state.invitations = (response.data || []).map(invitationToPendingInvitation);
            } catch (error) {
              const { fieldErrors, serverMessage } = ParseServerError(error);

              if (fieldErrors) {
                state.errors = ProcessServerErrors(fieldErrors);
              } else if (serverMessage) {
                notifyError({ text: serverMessage });
              } else {
                notifyError({ text: "Failed to fetch invitations." });
              }
            } finally {
              state.loading = false;
            }
        },

        /**
         * Invite one or more addresses to a team. Scoped to a single team when teamID is given,
         * otherwise invited to the organization without a team. Omitting the role lets the server
         * apply its default. Newly invited addresses are appended to the list.
         *
         * Returns the invited/resent/skipped buckets on success and null on failure, with any
         * validation errors populated in the errors property, keyed by email address.
         */
        inviteUsers: async function ({
            orgID,
            emails,
            teamID = null,
            role = null,
            message = "",
        }: {
            orgID: string,
            emails: string[],
            teamID?: string | null,
            role?: TeamMemberRole | null,
            message?: string,
        }): Promise<TypedTeamInvitationList | null> {
            clearErrors();

            try {
                const scope = teamID ? `/team/${teamID}` : "";
                const url = `/app/api/organization/${orgID}${scope}/invite`;
                const response = await router.post<TypedTeamInvitationList>(url, {
                    invitations: emails,
                    ...(role && { role }),
                    message,
                }, { DISABLE_ERROR_INTERCEPTORS: true });

                const result: TypedTeamInvitationList = {
                    success: response.data?.success ?? true,
                    invited: response.data?.invited || [],
                    resent: response.data?.resent || [],
                    errors: response.data?.errors || {},
                };

                // Only the newly invited are appended; a resent address is already in the list.
                state.invitations.push(...result.invited.map(invitationToPendingInvitation));

                return result;
            } catch (e) {
                const { fieldErrors, serverMessage, status } = ParseServerError(e);

                if (status === 422 && fieldErrors) {
                    state.errors = mapInvitationErrors(fieldErrors, emails);
                } else if (fieldErrors) {
                    state.errors = ProcessServerErrors(fieldErrors);
                } else if (serverMessage) {
                    notifyError({ text: serverMessage });
                } else {
                    notifyError({ text: "Failed to invite user." });
                }

                return null;
            }
        },

        /**
         * Resend a pending invitation. Scoped to a single team when teamID is given, otherwise
         * resent against the organization.
         *
         * The server throttles resends per invitation, so a recently sent invitation fails with a
         * message saying how long to wait. Returns false on failure with the reason notified.
         */
        resendInvitation: async function ({
            orgID,
            teamID = null,
            invitationID,
        }: { orgID: string, teamID?: string | null, invitationID: string }): Promise<boolean> {
            clearErrors();

            try {
                const url = `${invitationBaseUrl(orgID, teamID)}/${invitationID}/resend`;
                const response = await router.post<TypedTeamInvitation>(
                    url,
                    { _method: "PUT" },
                    { DISABLE_ERROR_INTERCEPTORS: true },
                );

                // The invitation comes back with a refreshed last_sent_at, which the throttle runs
                // from. The organization endpoint answers with a different shape, so only remap
                // when the payload is a team invitation; the row refreshes on the next fetch.
                const invitation = response.data;
                if (invitation?.invite_id) {
                    state.invitations = state.invitations.map(
                        (pending) => pending.invitation_id === invitationID
                            ? invitationToPendingInvitation(invitation)
                            : pending,
                    );
                }

                notifySuccess({ text: "Invitation has been resent." });

                return true;
            } catch (e) {
                logError(`[resendInvitation]: Could not resend the invitation ${invitationID} from org: ${orgID} and team: ${teamID}`);
                notifyInvitationFailure(e, "Could not resend the invitation.");

                return false;
            }
        },

        /**
         * Cancel a pending invitation. Scoped to a single team when teamID is given, otherwise
         * cancelled against the organization. Returns false on failure with the reason notified.
         */
        cancelInvitation: async function ({
            orgID,
            invitationID,
            teamID = null,
        }: { orgID: string, invitationID: string, teamID?: string | null }): Promise<boolean> {
            clearErrors();

            try {
                const url = `${invitationBaseUrl(orgID, teamID)}/${invitationID}`;
                await router.delete(url, null, { DISABLE_ERROR_INTERCEPTORS: true });

                state.invitations = state.invitations.filter(
                    (pending) => pending.invitation_id !== invitationID,
                );
                notifySuccess({ text: "Invitation has been cancelled." });

                return true;
            } catch (e) {
                logError(`[cancelInvitation]: Could not cancel the invitation ${invitationID} from org: ${orgID} and team: ${teamID}`);
                notifyInvitationFailure(e, "Could not cancel the invitation.");

                return false;
            }
        },

        /**
         * Allows you to swap out the router for a fake one when writing tests
         */
        setRouter(newRouter: HttpRouter<"get" | "post" | "delete">) {
            router = newRouter;
            return that;
        },
    };

    return that;
}
